import type { ReactNode } from 'react';
import { color, fontSize, fontWeight, radius } from './tokens';
import { Button, Card, Spinner } from './primitives';

/**
 * The five states every data surface must render (backlog "cross-cutting
 * definition of done": loading, empty, failure, retry, permission).
 *
 * They live here rather than in each feature so a screen cannot ship with only
 * the happy path. If you are writing a list and have not used one of these,
 * the list has an unhandled state.
 */

function Frame({
  icon,
  title,
  body,
  action,
  tone = 'neutral',
}: {
  icon: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  tone?: 'neutral' | 'danger' | 'warning' | 'brand';
}) {
  const fg =
    tone === 'danger'
      ? color.status.danger.fg
      : tone === 'warning'
        ? color.status.warning.fg
        : tone === 'brand'
          ? color.brand.primary
          : color.text.muted;
  const bg =
    tone === 'danger'
      ? color.status.danger.bg
      : tone === 'warning'
        ? color.status.warning.bg
        : tone === 'brand'
          ? color.brand.soft
          : color.surface.muted;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '56px 24px',
        gap: '10px',
      }}
    >
      <div
        aria-hidden
        style={{
          width: '52px',
          height: '52px',
          borderRadius: radius.xl,
          background: bg,
          color: fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: fontSize['4xl'],
          marginBottom: '4px',
        }}
      >
        {icon}
      </div>
      <div style={{ fontSize: fontSize.xl, fontWeight: fontWeight.black }}>{title}</div>
      {body ? (
        <div
          style={{
            fontSize: fontSize.md,
            color: color.text.muted,
            maxWidth: '440px',
            lineHeight: 1.7,
          }}
        >
          {body}
        </div>
      ) : null}
      {action ? <div style={{ marginTop: '8px' }}>{action}</div> : null}
    </div>
  );
}

export function LoadingState({ label = 'جارٍ التحميل…' }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        padding: '56px 24px',
        color: color.text.muted,
        fontSize: fontSize.md,
      }}
    >
      <Spinner />
      {label}
    </div>
  );
}

/**
 * Skeleton rows for a table that is loading.
 *
 * Preferred over a spinner where the shape of the result is already known: it
 * stops the layout jumping when the data lands, which matters on screens
 * reception reads dozens of times a day.
 */
export function SkeletonRows({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div aria-hidden style={{ padding: '8px 0' }}>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} style={{ display: 'flex', gap: '18px', padding: '12px 18px' }}>
          {Array.from({ length: columns }).map((__, c) => (
            <div
              key={c}
              style={{
                flex: c === 0 ? 2 : 1,
                height: '13px',
                borderRadius: radius.xs,
                background: `linear-gradient(90deg, ${color.surface.muted} 25%, ${color.border.muted} 37%, ${color.surface.muted} 63%)`,
                backgroundSize: '400% 100%',
                animation: 'medos-shimmer 1.4s ease infinite',
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
  icon = '◌',
}: {
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return <Frame icon={icon} title={title} body={body} action={action} />;
}

/**
 * A failed request.
 *
 * `correlationId` is shown deliberately: PRD 4.3 targets 100% of failed
 * operations carrying one, and it is the only thing that lets support find the
 * request in the logs without asking the clinic to reproduce the problem.
 */
export function ErrorState({
  title = 'تعذّر تحميل البيانات',
  body,
  correlationId,
  onRetry,
}: {
  title?: ReactNode;
  body?: ReactNode;
  correlationId?: string;
  onRetry?: () => void;
}) {
  return (
    <Frame
      tone="danger"
      icon="▲"
      title={title}
      body={
        <>
          {body ?? 'حدث خطأ غير متوقع. يمكنك إعادة المحاولة.'}
          {correlationId ? (
            <div style={{ marginTop: '10px', fontSize: fontSize.sm, color: color.text.faint }}>
              معرّف الطلب:{' '}
              <span dir="ltr" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                {correlationId}
              </span>
            </div>
          ) : null}
        </>
      }
      action={
        onRetry ? (
          <Button variant="secondary" onClick={onRetry}>
            إعادة المحاولة
          </Button>
        ) : null
      }
    />
  );
}

/**
 * The user is authenticated but not authorised.
 *
 * Says which role can help rather than only refusing (PRD 15.2: users without
 * permission get a safe message and a route to an authorised role).
 */
export function PermissionDenied({
  body = 'ليس لديك صلاحية لعرض هذه الشاشة.',
  contactRole,
}: {
  body?: ReactNode;
  contactRole?: string;
}) {
  return (
    <Frame
      tone="warning"
      icon="⊘"
      title="غير مصرّح"
      body={
        <>
          {body}
          {contactRole ? (
            <div style={{ marginTop: '8px' }}>راجع {contactRole} لطلب الصلاحية.</div>
          ) : null}
        </>
      }
    />
  );
}

/**
 * HTTP 402. The tenant's subscription blocks this action.
 *
 * Distinct from PermissionDenied on purpose: the fix is commercial, not
 * administrative, and the upgrade route has to be reachable from here
 * (PRD 8.4: limit errors return used, limit, reset date and upgrade route).
 */
export function SubscriptionBlocked({
  title = 'هذه الميزة غير متاحة في باقتك',
  used,
  limit,
  resetsAt,
  onUpgrade,
}: {
  title?: ReactNode;
  used?: number;
  limit?: number;
  resetsAt?: string;
  onUpgrade?: () => void;
}) {
  return (
    <Card style={{ padding: '24px', textAlign: 'center' }}>
      <Frame
        tone="brand"
        icon="◈"
        title={title}
        body={
          <>
            {typeof used === 'number' && typeof limit === 'number' ? (
              <div>
                الاستخدام الحالي{' '}
                <span dir="ltr" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  {used} / {limit === -1 ? '∞' : limit}
                </span>
              </div>
            ) : null}
            {resetsAt ? <div style={{ marginTop: '6px' }}>يُعاد الاحتساب في {resetsAt}</div> : null}
          </>
        }
        action={
          onUpgrade ? (
            <Button variant="primary" onClick={onUpgrade}>
              ترقية الباقة
            </Button>
          ) : null
        }
      />
    </Card>
  );
}

/**
 * Optimistic-concurrency conflict (HTTP 409).
 *
 * Never auto-resolves. VIS-025 requires the user keep their unsaved text and
 * choose, because the alternative is silently discarding clinical notes.
 */
export function ConflictNotice({
  onReload,
  onKeepEditing,
}: {
  onReload: () => void;
  onKeepEditing: () => void;
}) {
  return (
    <div
      role="alert"
      style={{
        border: `1px solid ${color.status.warning.border}`,
        background: color.status.warning.bg,
        borderRadius: radius.lg,
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
      }}
    >
      <span aria-hidden style={{ color: color.status.warning.fg, fontWeight: fontWeight.black }}>
        !
      </span>
      <div style={{ flex: 1, minWidth: '240px', fontSize: fontSize.base, lineHeight: 1.7 }}>
        عدّل مستخدم آخر هذا السجل بعد أن فتحته. لم يُحفظ ما كتبته بعد.
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button size="sm" variant="secondary" onClick={onKeepEditing}>
          متابعة التحرير
        </Button>
        <Button size="sm" variant="primary" onClick={onReload}>
          إعادة التحميل والمقارنة
        </Button>
      </div>
    </div>
  );
}

/**
 * A non-critical dependency is down (realtime, messaging, AI, preview).
 *
 * PRD 21.1 requires these degrade rather than block, so this is a banner the
 * screen renders *alongside* working content - never instead of it.
 */
export function DegradedBanner({ children }: { children: ReactNode }) {
  return (
    <div
      role="status"
      style={{
        background: color.status.warning.bg,
        color: color.status.warningStrong,
        borderRadius: radius.lg,
        padding: '10px 14px',
        fontSize: fontSize.base,
        marginBottom: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
      }}
    >
      <span aria-hidden>◷</span>
      {children}
    </div>
  );
}
